import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { isCeremonyCancelled, passkeysSupported, signInWithPasskey } from "../../auth/passkeys.js";
import { landingFor, setSession } from "../../auth/session.js";
import { AuthLayout, Button, ErrorNotice, Field, Input } from "../../components/form.js";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { domain } = useSearch({ from: "/public/login" });

  /** A domain picked in the storefront goes straight to checkout once an administrator is in. */
  const continueAfter = (session: Parameters<typeof landingFor>[0]) => {
    setSession(queryClient, session);
    const isAdmin = session.user.role === "owner" || session.user.role === "admin";
    if (domain && isAdmin && !session.mfa.pending) {
      void navigate({ to: "/admin/domains/checkout", search: { domain, years: 1 } });
    } else {
      void navigate({ to: landingFor(session) });
    }
  };

  const login = useMutation({
    mutationFn: () => api.auth.login({ email, password }),
    onSuccess: continueAfter,
  });

  const passkey = useMutation({
    mutationFn: () => signInWithPasskey(),
    onSuccess: continueAfter,
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    login.mutate();
  };

  return (
    <AuthLayout title={t("auth.login.title")} subtitle={t("auth.login.subtitle")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label={t("auth.fields.email")} hint={t("auth.login.emailHint")}>
          <Input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t("auth.fields.password")}>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <ErrorNotice error={login.error} />
        <Button type="submit" disabled={login.isPending}>
          {login.isPending ? t("common.loading") : t("auth.login.submit")}
        </Button>
        <p className="text-center text-sm">
          <Link
            to="/forgot-password"
            className="text-ink-3 underline-offset-2 hover:text-ink hover:underline"
          >
            {t("auth.login.forgot")}
          </Link>
        </p>
        {passkeysSupported() ? (
          <>
            <div className="flex items-center gap-3 text-xs text-ink-3">
              <span className="h-px flex-1 bg-rule" />
              {t("auth.login.or")}
              <span className="h-px flex-1 bg-rule" />
            </div>
            {passkey.error && !isCeremonyCancelled(passkey.error) ? (
              <ErrorNotice error={passkey.error} />
            ) : null}
            <Button
              variant="secondary"
              disabled={passkey.isPending}
              onClick={() => passkey.mutate()}
            >
              {t("auth.login.passkey")}
            </Button>
          </>
        ) : null}
        <p className="text-center text-sm text-ink-2">
          {t("auth.login.noAccount")}{" "}
          <Link to="/signup" className="text-accent underline-offset-2 hover:underline">
            {t("auth.login.createWorkspace")}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
